<script setup>
import { shallowRef, ref, computed, watch, nextTick } from 'vue';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';
import emojiGroups from 'shared/components/emoji/emojisGroup.json';

const props = defineProps({
  searchKey: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['selectEmoji']);

// Pre-process emoji data eagerly during setup so items are available
// on the very first render (avoids two-render issue with onMounted)
const allEmojis = shallowRef(
  emojiGroups.flatMap(({ emojis }) =>
    emojis.map(({ name, slug, ...rest }) => ({
      ...rest,
      name,
      slug,
      searchString: `${name.replace(/\s+/g, '')} ${slug}`.toLowerCase(),
    }))
  )
);

const MAX_RESULTS = 20;

const items = computed(() => {
  if (!props.searchKey) return [];
  const searchTerm = props.searchKey.toLowerCase();
  return allEmojis.value
    .filter(emoji => emoji.searchString.includes(searchTerm))
    .slice(0, MAX_RESULTS);
});

const selectedIndex = ref(0);
const stripRef = ref(null);

// Reset selection when items change
watch(
  () => items.value,
  newItems => {
    if (newItems.length < selectedIndex.value + 1) {
      selectedIndex.value = 0;
    }
  }
);

function scrollSelectedIntoView() {
  nextTick(() => {
    const container = stripRef.value;
    if (!container) return;
    const el = container.querySelector(
      `[data-emoji-index="${selectedIndex.value}"]`
    );
    if (el) {
      el.scrollIntoView({
        inline: 'nearest',
        block: 'nearest',
        behavior: 'auto',
      });
    }
  });
}

watch(selectedIndex, scrollSelectedIntoView);

function selectCurrent() {
  if (items.value.length > 0) {
    emit('selectEmoji', items.value[selectedIndex.value].emoji);
  }
}

function moveLeft() {
  if (!items.value.length) return;
  selectedIndex.value =
    selectedIndex.value === 0
      ? items.value.length - 1
      : selectedIndex.value - 1;
}

function moveRight() {
  if (!items.value.length) return;
  selectedIndex.value =
    selectedIndex.value === items.value.length - 1
      ? 0
      : selectedIndex.value + 1;
}

const createAction = action => ({
  action: e => {
    action();
    e.preventDefault();
  },
  allowOnFocusedInput: true,
});

useKeyboardEvents({
  ArrowLeft: createAction(moveLeft),
  ArrowRight: createAction(moveRight),
  Enter: createAction(selectCurrent),
});
</script>

<!-- eslint-disable-next-line vue/no-root-v-if -->
<template>
  <div v-if="items.length" ref="stripRef" class="emoji-strip">
    <button
      v-for="(item, index) in items"
      :key="item.slug"
      :data-emoji-index="index"
      :title="item.name"
      class="emoji-strip__item"
      :class="{ 'emoji-strip__item--selected': index === selectedIndex }"
      @click="emit('selectEmoji', item.emoji)"
      @mouseover="selectedIndex = index"
    >
      {{ item.emoji }}
    </button>
  </div>
</template>

<style scoped lang="scss">
.emoji-strip {
  @apply flex items-center gap-0.5 absolute left-0 bottom-full z-20
    px-1.5 py-1 mb-1 rounded-lg
    bg-n-solid-3 border border-solid border-n-strong
    shadow-md overflow-x-auto;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
}

.emoji-strip__item {
  @apply flex items-center justify-center shrink-0
    w-8 h-8 rounded-md text-lg leading-none
    cursor-pointer select-none
    transition-colors duration-100;

  &:hover,
  &--selected {
    @apply bg-n-alpha-black2;
  }

  &--selected {
    @apply ring-1 ring-n-brand;
  }
}
</style>
